import { v4 as uuidv4 } from "uuid";

import { loadAndResizeImageDataURL } from "../../common/components/form/ImageInput";

const { post, unwrapError } = require("../../common/utils/api");
const { db } = require("./data");

export function errorMessage(message) {
  return {
    response: {
      data: message,
    },
  };
}

export async function generateThumbnail(dataURI) {
  // Use the existing loadAndResizeImageDataURL function
  const result = await loadAndResizeImageDataURL({
    dataURL: dataURI,
    maxSize: Infinity, // making it infinite to facilitate mocking
  });

  return result.thumbnail;
}

function dataURItoBlob(dataURI) {
  const byteString = atob(dataURI.split(",")[1]);
  const mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: mimeString });
}

async function UploadToS3(presignedUrl, imageBlob, onProgress, i, name, c) {
  // PUT to the S3 URL using XMLHttpRequest for per-image progress
  await new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", presignedUrl, true);
      xhr.setRequestHeader("Content-Type", imageBlob.type);

      // Notify start for this image
      if (typeof onProgress === "function") {
        try {
          onProgress({ type: "image_start", index: i });
        } catch (_) {}
      }

      xhr.upload.onprogress = (e) => {
        if (typeof onProgress === "function") {
          try {
            onProgress({
              type: "image_progress",
              index: i,
              loaded: e.loaded ?? 0,
              total: e.total ?? 0,
              percent:
                e.total > 0 ? Math.floor((e.loaded / e.total) * 100) : null,
            });
          } catch (_) {}
        }
      };

      xhr.onload = async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          if (typeof onProgress === "function") {
            try {
              onProgress({ type: "image_complete", index: i });
            } catch (_) {}
          }
          // Yield to the event loop to allow UI to render intermediate progress
          setTimeout(() => resolve(), 0);
        } else {
          const errMsg = `S3 upload failed for ${name} image #${
            i + 1
          } for crop #${c + 1}`;
          reject(errorMessage(errMsg));
        }
      };

      xhr.onerror = () => {
        reject(
          errorMessage(
            `Network error during upload for ${name} image #${
              i + 1
            } for crop #${c + 1}`,
          ),
        );
      };

      xhr.send(imageBlob);
    } catch (err) {
      reject(
        errorMessage(
          `Failed to initiate upload for ${name} image #${i + 1} for crop #${
            c + 1
          }`,
        ),
      );
    }
  });
}

async function process(images, c, name, onProgress, imageStatusMap) {
  if (!images || images.length === 0) return;

  for (let i = 0; i < images.length; i++) {
    try {
      const imageId = images[i]?.id;
      if (!imageId) continue;
      const imageObj = await db.images.get(imageId);
      let { image, image_key: imageKey, thumbnail } = imageObj;
      const imageStatus = imageStatusMap.get(imageKey);
      if (imageStatus?.presigned_post) {
        if (!image || typeof image !== "string" || !image.startsWith("data:")) {
          throw errorMessage(
            `Invalid ${name} image #${i + 1} for crop #${c + 1}`,
          );
        }
        if (!thumbnail) {
          thumbnail = await generateThumbnail(image);
          await db.images.update(imageId, {
            thumbnail,
          });
        }
        const { url: presignedUrl } = imageStatus.presigned_post;
        if (presignedUrl) {
          const imageBlob = dataURItoBlob(image);
          await UploadToS3(presignedUrl, imageBlob, onProgress, i, name, c);
        }
        // Clear local data only after a successful upload
        await db.images.update(imageId, { image: undefined });
      } else if (imageStatus?.status === "EXISTS" && image) {
        // Image already exists on server, clear local data
        await db.images.update(imageId, { image: undefined });
      }
    } catch (e) {
      // Preserve specific error types instead of overriding with generic message
      const prefixesToKeep = ["Invalid", "S3", "Network", "Failed", "App"];
      const errorMessageText = e?.response?.data;
      if (
        errorMessageText &&
        prefixesToKeep.some((p) => errorMessageText.startsWith(p))
      ) {
        throw e;
      }
      throw errorMessage(
        `Error with ${name} image #${i + 1} for crop #${c + 1}`,
      );
    }
  }
}

const imageFields = [
  { path: ["images"], name: "crop" },
  { path: ["boundary_images"], name: "boundary_crop" },
  { path: ["disease_images"], name: "disease" },
  // Legacy
  { path: ["yield_images"], name: "yield" },
  { path: ["yield", "yield_images"], name: "yield" },
  { path: ["yield", "size_images"], name: "size" },
  { path: ["yield", "diagonal_images"], name: "diagonal" },
  { path: ["yield", "harvested_images"], name: "harvested" },
  { path: ["yield", "moisture_images"], name: "moisture" },
  { path: ["yield", "yield_w1_images"], name: "yield w1" },
  { path: ["yield", "yield_w2_images"], name: "yield w2" },
  { path: ["yield", "yield_w3_images"], name: "yield w3" },
  { path: ["yield", "yield_w4_images"], name: "yield w4" },
  { path: ["yield", "yield_w4_drying_images"], name: "yield w4 drying" },
  { path: ["yield", "yield_w5_images"], name: "yield w5" },
];

async function forEachImageArrayInRecord(d, callback) {
  function getPropertyByPath(obj, path) {
    return path.reduce((current, key) => current?.[key], obj);
  }
  if (!d.crops) return;
  for (let c = 0; c < d.crops.length; c++) {
    const crop = d.crops[c];
    for (const field of imageFields) {
      const imageArray = getPropertyByPath(crop, field.path);
      await callback(imageArray, c, field.name);
    }
  }
}

export async function processImages(d, onProgress, imageStatusMap) {
  // Process all crops and their image types
  try {
    await forEachImageArrayInRecord(d, (imageArray, c, name) =>
      process(imageArray, c, name, onProgress, imageStatusMap),
    );
  } catch (error) {
    throw error;
  }
}

export async function getUploadUrls(d) {
  const imagesToProcess = [];
  await forEachImageArrayInRecord(
    d,
    async (imageArray, cropIndex, fieldName) => {
      if (!imageArray || imageArray.length === 0) return;

      for (let i = 0; i < imageArray.length; i++) {
        const imageId = imageArray[i]?.id;
        if (!imageId) continue;
        const imageObj = await db.images.get(imageId);

        if (imageObj && imageObj.image) {
          let { image, image_key: imageKey } = imageObj;
          // Check for invalid image data format *before* making an API call.
          if (
            !image ||
            typeof image !== "string" ||
            !image.startsWith("data:")
          ) {
            throw errorMessage(
              `Invalid ${fieldName} image #${i + 1} for crop #${cropIndex + 1}`,
            );
          }

          // If validation passes, proceed as before.
          const mimeString = image.split(",")[0].split(":")[1].split(";")[0];

          if (!imageKey) {
            const ext = image.split(";base64,")[0].split("/")[1];
            imageKey = `${uuidv4()}.${ext}`;
            await db.images.update(imageId, { image_key: imageKey });
          }
          imagesToProcess.push({
            image_key: imageKey,
            content_type: mimeString,
          });
        }
      }
    },
  );

  if (imagesToProcess.length === 0) {
    return [];
  }

  const { data: imagesWithStatus } = await post(
    "/image/push/",
    imagesToProcess,
  );
  return imagesWithStatus;
}

export async function processImagesForRecord(
  d,
  index,
  arr,
  onProgress,
  addTotalToProgress,
) {
  if (typeof onProgress === "function") {
    try {
      onProgress({
        type: "record_change",
        index,
        total: arr.length,
        phase: "getting",
      });
    } catch (_) {}
  }
  const imagesWithStatus = await getUploadUrls(d);
  if (addTotalToProgress) {
    const imagesToUpload = imagesWithStatus.filter(
      (item) => item.presigned_post,
    );
    addTotalToProgress(imagesToUpload.length);
  }
  if (typeof onProgress === "function") {
    try {
      onProgress({
        type: "record_change",
        index,
        total: arr.length,
        phase: "uploading",
      });
    } catch (_) {}
  }
  const imageStatusMap = new Map(
    imagesWithStatus.map((item) => [item.image_key, item]),
  );
  await processImages(d, onProgress, imageStatusMap);
}

export async function syncImages(onProgress, addTotal, options = {}) {
  const { onError, continueOnError } = options || {};
  try {
    const data = await db.data.toArray();

    for (let idx = 0; idx < data.length; idx++) {
      const d = data[idx];
      try {
        await processImagesForRecord(d, idx, data, onProgress, addTotal);
      } catch (error) {
        if (typeof onError === "function") {
          const message =
            (error && error.response && error.response.data) ||
            (typeof unwrapError === "function"
              ? unwrapError(error)
              : String(error));
          try {
            onError({ recordId: d.id, recordName: d.name, message });
          } catch (_) {}
        }
        if (!continueOnError) {
          throw error;
        }
      }
    }
  } catch (error) {
    throw error;
  }
}

// Utility to traverse and remove specific fields
export function traverseAndRemove(obj) {
  if (!obj || typeof obj !== "object") return;

  if (Array.isArray(obj)) {
    for (const item of obj) {
      traverseAndRemove(item);
    }
  } else {
    for (const key in obj) {
      if (typeof obj[key] === "object") {
        // If the key indicates an image-related array, remove "id" and "thumbnail" from its objects
        if (Array.isArray(obj[key]) && /images$/.test(key)) {
          for (const imageObj of obj[key]) {
            if (typeof imageObj === "object") {
              delete imageObj.id;
              delete imageObj.thumbnail;
            }
          }
        }
        traverseAndRemove(obj[key]); // Continue traversal
      }
    }
  }
}
